/**
 * TaskDetailModal — opens when a task row is clicked.
 *
 * Sections:
 *  - Title + description
 *  - Assigned by / to, due / created cards
 *  - File download (if any)
 *  - Status dropdown (assignee or assigner). Optional feedback textarea below.
 *  - Feedback thread — all messages (with author + timestamp), newest at the bottom.
 *
 * Props:
 *  - task          : task object from /api/tasks/inbox or /api/tasks/sent
 *  - canChange     : boolean — show the status dropdown (assignee or assigner)
 *  - canRenotify   : boolean — show the Notify Again button (assigner only)
 *  - currentUserId : id of the logged-in user (for "Me" labelling of feedback rows)
 *  - onStatusChange: async (newTask) => void  — caller refetches after
 *  - onRenotify    : async () => void
 *  - onClose       : () => void
 */
import { useEffect, useRef, useState } from 'react';
import { X, Loader2, Download, Bell, Calendar, UserCircle, Send } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const STATUS_OPTIONS = [
  { value: 'new',     label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'done',    label: 'Done' },
];

const TaskDetailModal = ({
  task: initialTask,
  canChange, canRenotify, currentUserId,
  onStatusChange, onRenotify, onClose,
}) => {
  const [task, setTask] = useState(initialTask);
  const [statusDraft, setStatusDraft] = useState(initialTask.status);
  const [feedbackDraft, setFeedbackDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);

  const feedbacks = task.feedbacks || [];

  useEffect(() => {
    // Scroll feedback thread to the bottom whenever it grows
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [feedbacks.length]);

  const applyChanges = async () => {
    const statusChanged = statusDraft !== task.status;
    const feedbackText = feedbackDraft.trim();
    if (!statusChanged && !feedbackText) return;

    setBusy(true);
    try {
      let updatedTask = task;
      if (statusChanged) {
        const r = await axios.patch(`${API}/tasks/${task.id}/status`, {
          status: statusDraft,
          feedback: feedbackText || undefined,
        });
        updatedTask = r.data.task;
        toast.success(feedbackText
          ? `Status changed to ${statusDraft} · feedback sent`
          : `Status changed to ${statusDraft}`);
      } else if (feedbackText) {
        const r = await axios.post(`${API}/tasks/${task.id}/feedback`, { message: feedbackText });
        updatedTask = r.data.task;
        toast.success('Feedback sent');
      }
      setTask(updatedTask);
      setFeedbackDraft('');
      setStatusDraft(updatedTask.status);
      await onStatusChange?.(updatedTask);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const handleRenotify = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tasks/${task.id}/notify`);
      toast.success('Assignee has been re-notified');
      await onRenotify?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to notify');
    } finally {
      setBusy(false);
    }
  };

  const downloadFile = () => {
    fetch(`${API}/tasks/${task.id}/file`, { credentials: 'include' })
      .then((r) => r.ok ? r.blob() : Promise.reject(new Error('Download failed')))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = task.file_name || 'file'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => toast.error('Could not download the file'));
  };

  const dirty = statusDraft !== task.status || !!feedbackDraft.trim();

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-6" data-testid="task-detail-modal">
      <div className="bg-white rounded-3xl border border-mute/30 shadow-card-hover max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between px-8 py-6 border-b border-mute/20">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-ink/50 tracking-wide">Task</div>
            <h2 className="font-display text-2xl font-bold text-ink tracking-tight mt-1">{task.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-ink/5 text-ink/60 shrink-0 ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-8 py-6 space-y-6">
          {task.description ? (
            <div>
              <div className="text-xs font-semibold text-ink/50 tracking-wide mb-2">Description</div>
              <p className="text-sm text-ink/80 whitespace-pre-wrap leading-relaxed bg-surface-50 rounded-2xl p-4 border border-mute/20">
                {task.description}
              </p>
            </div>
          ) : (
            <div className="text-sm italic text-ink/40">No description.</div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-surface-50 rounded-xl p-4 border border-mute/20">
              <div className="flex items-center gap-2 text-xs font-semibold text-ink/50 tracking-wide mb-1">
                <UserCircle className="w-3.5 h-3.5" /> Assigned by
              </div>
              <div className="text-ink font-medium">{task.assigned_by_name || '—'}</div>
              {task.assigned_by_email && <div className="text-xs text-ink/50">{task.assigned_by_email}</div>}
            </div>
            <div className="bg-surface-50 rounded-xl p-4 border border-mute/20">
              <div className="flex items-center gap-2 text-xs font-semibold text-ink/50 tracking-wide mb-1">
                <UserCircle className="w-3.5 h-3.5" /> Assigned to
              </div>
              <div className="text-ink font-medium">{task.assigned_to_name || '—'}</div>
              {task.assigned_to_email && <div className="text-xs text-ink/50">{task.assigned_to_email}</div>}
            </div>
            <div className="bg-surface-50 rounded-xl p-4 border border-mute/20">
              <div className="flex items-center gap-2 text-xs font-semibold text-ink/50 tracking-wide mb-1">
                <Calendar className="w-3.5 h-3.5" /> Due date
              </div>
              <div className="text-ink font-medium">{fmtDate(task.due_date)}</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-4 border border-mute/20">
              <div className="text-xs font-semibold text-ink/50 tracking-wide mb-1">Created</div>
              <div className="text-ink font-medium">{fmtDate(task.created_at)}</div>
              {task.completed_at && (
                <div className="text-xs text-emerald-700 mt-0.5">Completed {fmtDate(task.completed_at)}</div>
              )}
            </div>
          </div>

          {task.file_name && (
            <div>
              <div className="text-xs font-semibold text-ink/50 tracking-wide mb-2">Attachment</div>
              <button
                data-testid="modal-download-file"
                onClick={downloadFile}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-primary text-sm font-semibold hover:bg-blue-100 transition-colors"
              >
                <Download className="w-4 h-4" />
                {task.file_name}
              </button>
            </div>
          )}

          {/* ===== Status + feedback ===== */}
          <div className="pt-5 border-t border-mute/20 space-y-4">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs font-semibold text-ink/50 tracking-wide mb-1.5">Status</div>
                {canChange ? (
                  <Select value={statusDraft} onValueChange={setStatusDraft} disabled={busy}>
                    <SelectTrigger data-testid="modal-status-select" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="px-4 py-2.5 rounded-xl bg-surface-50 border border-mute/20 text-sm font-medium capitalize text-ink/70">
                    {task.status}
                  </div>
                )}
              </div>

              {canRenotify && task.status !== 'done' && (
                <button
                  data-testid="modal-renotify"
                  onClick={handleRenotify}
                  disabled={busy}
                  className="h-11 px-5 rounded-full bg-ink/5 text-ink/80 font-semibold text-sm hover:bg-ink/10 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  Notify Again
                </button>
              )}
            </div>

            {canChange && (
              <div>
                <label className="text-xs font-semibold text-ink/50 tracking-wide mb-1.5 block">
                  Response / Feedback <span className="text-ink/30 font-normal">(optional — sent to {currentUserId === task.assigned_to ? task.assigned_by_name : task.assigned_to_name})</span>
                </label>
                <textarea
                  data-testid="feedback-input"
                  rows={3}
                  value={feedbackDraft}
                  onChange={(e) => setFeedbackDraft(e.target.value)}
                  placeholder="Add a note for the other party…"
                  className="w-full bg-white border-2 border-mute/30 focus:border-primary focus:outline-none rounded-xl px-4 py-3 text-sm text-ink placeholder:text-ink/40 resize-none transition-colors"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    data-testid="apply-changes"
                    onClick={applyChanges}
                    disabled={busy || !dirty}
                    className="h-11 px-6 rounded-full bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {statusDraft !== task.status && feedbackDraft.trim()
                      ? 'Update status & send'
                      : statusDraft !== task.status
                        ? 'Update status'
                        : 'Send feedback'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ===== Feedback thread ===== */}
          {feedbacks.length > 0 && (
            <div className="pt-5 border-t border-mute/20">
              <div className="text-xs font-semibold text-ink/50 tracking-wide mb-3">
                Conversation · {feedbacks.length}
              </div>
              <div
                ref={threadRef}
                className="space-y-2.5 max-h-64 overflow-y-auto pr-2"
                data-testid="feedback-thread"
              >
                {feedbacks.map((f) => {
                  const mine = f.by_user_id === currentUserId;
                  return (
                    <div
                      key={f.id}
                      className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        mine ? 'bg-primary text-white' : 'bg-surface-100 text-ink'
                      }`}>
                        <div className={`text-[11px] font-semibold mb-0.5 ${mine ? 'text-white/70' : 'text-ink/50'}`}>
                          {mine ? 'You' : f.by_name} · {fmtAgo(f.at)}
                          {f.with_status && (
                            <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${mine ? 'bg-white/20' : 'bg-ink/10'}`}>
                              {f.with_status}
                            </span>
                          )}
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{f.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
